import type { CSSProperties } from 'react'
import { useNavigate } from 'react-router-dom'
import Challenge from '../model/Challenge'
import { useChallenges } from '../hooks/challenge/useChallenges'
import { useEntitledChallengeIds } from '../hooks/membership/useEntitledChallengeIds'
import { useTeamMemberships } from '../hooks/membership/useTeamMemberships'
import { challengeTeamPath, ScreenPath } from '../navigation'
import AppIcon from '../ui/icons/AppIcon'
import ChallengeMark from '../ui/challenge/ChallengeMark'
import ChallengeStartsIn from '../ui/challenge/ChallengeStartsIn'
import styles from '../ui/challenge/ChallengeJoin.module.css'
import SimplePageLayout from '../ui/layout/SimplePageLayout'
import Eyebrow from '../ui/primitives/Eyebrow'
import LoadingIndicator from '../ui/util/LoadingIndicator'
import DateUtil from '../util/DateUtil'

type ChallengePhase = 'upcoming' | 'live' | 'ended'

const PHASE_LABELS: Record<ChallengePhase, string> = {
  upcoming: 'Upcoming',
  live: 'In progress',
  ended: 'Ended',
}

function getPhase(challenge: Challenge): ChallengePhase {
  if (challenge.hasEnded()) return 'ended'
  if (challenge.hasStarted()) return 'live'
  return 'upcoming'
}

function dateRange(challenge: Challenge): string {
  const range = DateUtil.formatChallengeDateRange(challenge.startDate, challenge.endDate)
  const year = challenge.startDate.getFullYear()
  return year === new Date().getFullYear() ? range : `${range}, ${year}`
}

function ChallengePickCard({
  challenge,
  onSelect,
}: {
  challenge: Challenge
  onSelect: () => void
}) {
  const phase = getPhase(challenge)
  const accent = challenge.primaryColor || undefined

  return (
    <li>
      <button
        type="button"
        className={styles.card}
        style={accent ? ({ '--accent': accent } as CSSProperties) : undefined}
        onClick={onSelect}
      >
        <span className={styles.accent} aria-hidden />
        <span className={styles.head}>
          <ChallengeMark challenge={challenge} className={styles.markSlot} />
          <span className={styles.headText}>
            <span className={styles.name}>{challenge.name}</span>
            <span className={styles.meta}>
              {challenge.isTest && (
                <>
                  <span className={styles.testLabel}>Test</span>
                  <span className={styles.separator} aria-hidden />
                </>
              )}
              <span className={[styles.status, styles[phase]].join(' ')}>
                <span className={styles.dot} aria-hidden />
                {PHASE_LABELS[phase]}
              </span>
              <span className={styles.separator} aria-hidden />
              <span className={styles.dates}>{dateRange(challenge)}</span>
            </span>
          </span>
          <AppIcon icon="mdi:chevron-right" size={22} className={styles.chevron} />
        </span>
        {phase === 'upcoming' && (
          <ChallengeStartsIn startDate={challenge.startDate} className={styles.startsIn} />
        )}
        {challenge.description && (
          <span className={styles.description}>{challenge.description}</span>
        )}
      </button>
    </li>
  )
}

export default function ChallengeJoinScreen() {
  const navigate = useNavigate()
  const { data: challenges, isLoading } = useChallenges()
  const { data: memberships } = useTeamMemberships()
  const { data: entitledIds } = useEntitledChallengeIds()

  const listedIds = new Set((challenges ?? []).map((challenge) => challenge.id))
  const ownedIds = [
    ...(memberships?.map((m) => m.challengeID) ?? []),
    ...(entitledIds ?? []),
  ]
  const yourChallengeIds = new Set(
    challenges ? ownedIds.filter((id) => listedIds.has(id)) : ownedIds,
  )
  const joinable = (challenges ?? []).filter(
    (c) => !yourChallengeIds.has(c.id) && !c.hasEnded(),
  )
  const hasWorkspaces = yourChallengeIds.size > 0

  function selectChallenge(challengeId: string) {
    navigate(challengeTeamPath(challengeId))
  }

  const backTo = hasWorkspaces ? ScreenPath.home : undefined
  const title = hasWorkspaces ? 'Join another' : 'Select a challenge'
  const description = hasWorkspaces
    ? 'Pick a challenge to create or join a team.'
    : 'Log miles together, hit gates, and finish as a squad. Your challenge sets the route, the dates, and the goal.'

  if (isLoading) {
    return (
      <SimplePageLayout title={title} backTo={backTo}>
        <LoadingIndicator />
      </SimplePageLayout>
    )
  }

  return (
    <SimplePageLayout title={title} description={description} backTo={backTo}>
      <div className={styles.section}>
        <Eyebrow>Open challenges</Eyebrow>
      </div>

      {joinable.length === 0 ? (
        <div className={styles.empty}>
          <span className={styles.emptyIcon} aria-hidden>
            <AppIcon icon="mdi:flag-checkered" size={24} />
          </span>
          <p className={styles.emptyTitle}>Nothing open right now</p>
          <p className={styles.emptyCopy}>
            You&apos;re already in every available challenge, or none are open to join yet.
          </p>
        </div>
      ) : (
        <ul className={styles.list}>
          {joinable.map((challenge) => (
            <ChallengePickCard
              key={challenge.id}
              challenge={challenge}
              onSelect={() => selectChallenge(challenge.id)}
            />
          ))}
        </ul>
      )}
    </SimplePageLayout>
  )
}
